import { type MouseEvent, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface SelectMenuOption<T extends string = string> {
  value: T;
  label: string;
  /** Second, muted line under the label (e.g. an email under a name) — also
   * matched by `searchable` filtering, so typing it still finds the option. */
  sublabel?: string;
  /** 1-2 letter initials. Presence renders an avatar circle before the label
   * (LLMR-141's requester picker); omit for a plain text option. */
  avatarText?: string;
  /** Small trailing pill (e.g. "Free" on the Playground model picker). */
  badge?: string;
}

const GAP = 8;
const VIEW_MARGIN = 8;
const MAX_PANEL = 260;
const ITEM_HEIGHT = 42;

type PanelPlacement = {
  left: number;
  width: number;
  maxHeight: number;
  top?: number;
  bottom?: number;
};

function estimatePanelHeight(optionCount: number) {
  const listGap = Math.max(0, optionCount - 1) * 2;
  return optionCount * ITEM_HEIGHT + listGap + 12;
}

const cssPx = (value: string) => Number.parseFloat(value) || 0;

/**
 * Measure option label widths off-DOM. In-panel `scrollWidth` on inline
 * ellipsized spans is unreliable and left the panel stuck at trigger width
 * (e.g. "dungnapaPP (Owner)" clipped while "Test mini" fit).
 */
function measureWidestOptionLabel(
  labels: string[],
  fontSource: HTMLElement | null,
): number {
  if (labels.length === 0) return 0;
  const probe = document.createElement('span');
  probe.setAttribute('aria-hidden', 'true');
  probe.style.cssText =
    'position:absolute;left:-99999px;top:0;white-space:nowrap;visibility:hidden;pointer-events:none';
  if (fontSource) {
    const style = getComputedStyle(fontSource);
    probe.style.fontFamily = style.fontFamily;
    probe.style.fontSize = style.fontSize;
    probe.style.fontWeight = style.fontWeight;
    probe.style.fontStyle = style.fontStyle;
    probe.style.letterSpacing = style.letterSpacing;
    probe.style.fontVariationSettings = style.fontVariationSettings;
  }
  document.body.appendChild(probe);
  let widest = 0;
  for (const label of labels) {
    probe.textContent = label;
    widest = Math.max(widest, probe.offsetWidth);
  }
  probe.remove();
  return widest;
}

function measurePanelChrome(panelEl: HTMLElement): number {
  const item = panelEl.querySelector<HTMLElement>('.select-menu-item, .select-menu-empty');
  const list = panelEl.querySelector<HTMLElement>('.select-menu-list');
  const itemStyle = item ? getComputedStyle(item) : null;
  const panelStyle = getComputedStyle(panelEl);
  const listStyle = list ? getComputedStyle(list) : null;
  const itemPad = itemStyle ? cssPx(itemStyle.paddingLeft) + cssPx(itemStyle.paddingRight) : 0;
  const panelPad =
    cssPx(panelStyle.paddingLeft) +
    cssPx(panelStyle.paddingRight) +
    (listStyle ? cssPx(listStyle.paddingLeft) + cssPx(listStyle.paddingRight) : 0);
  // Active/hover rows use the full item box; include a small border fudge.
  const border =
    (itemStyle ? cssPx(itemStyle.borderLeftWidth) + cssPx(itemStyle.borderRightWidth) : 0) +
    cssPx(panelStyle.borderLeftWidth) +
    cssPx(panelStyle.borderRightWidth);
  return Math.ceil(itemPad + panelPad + border);
}

function placePanel(
  trigger: HTMLElement | null,
  optionLabels: string[],
  panelEl?: HTMLElement | null,
): PanelPlacement | null {
  if (!trigger) return null;
  const rect = trigger.getBoundingClientRect();
  const optionCount = Math.max(optionLabels.length, 1);
  const measuredHeight = panelEl?.offsetHeight ?? 0;
  const estimatedHeight = estimatePanelHeight(optionCount);
  const contentHeight = Math.max(measuredHeight, estimatedHeight);
  const panelHeight = Math.min(contentHeight, MAX_PANEL);

  const spaceBelow = window.innerHeight - rect.bottom - VIEW_MARGIN - GAP;
  const spaceAbove = rect.top - VIEW_MARGIN - GAP;

  const fontSource =
    panelEl?.querySelector<HTMLElement>('.select-menu-item-label, .select-menu-item, .select-menu-empty') ??
    trigger;
  const labelWidth = measureWidestOptionLabel(optionLabels, fontSource);
  const chrome = panelEl ? measurePanelChrome(panelEl) : 24;
  const contentWidth = labelWidth > 0 ? labelWidth + chrome : 0;
  const maxWidth = window.innerWidth - VIEW_MARGIN * 2;
  const width = Math.min(maxWidth, Math.max(rect.width, contentWidth));

  // Topbar controls sit on the right — when the panel grows past the trigger,
  // keep the right edges aligned so long labels expand leftward into free space.
  let left = width > rect.width && rect.left > window.innerWidth / 2 ? rect.right - width : rect.left;
  left = Math.min(Math.max(VIEW_MARGIN, left), window.innerWidth - width - VIEW_MARGIN);

  // Always open adjacent to the trigger (below preferred) — never cover it.
  const openBelow = spaceBelow >= panelHeight || spaceBelow >= spaceAbove;
  if (openBelow) {
    return {
      left,
      width,
      top: rect.bottom + GAP,
      maxHeight: Math.max(96, Math.min(MAX_PANEL, Math.floor(Math.max(spaceBelow, 96)))),
    };
  }

  return {
    left,
    width,
    bottom: window.innerHeight - rect.top + GAP,
    maxHeight: Math.max(96, Math.min(MAX_PANEL, Math.floor(Math.max(spaceAbove, 96)))),
  };
}

/**
 * Rounded single-select for forms.
 *
 * Native `<select>` popups are painted by the OS and cannot take a
 * `border-radius`, which is why the open list always looked square next to
 * our rounded inputs. This keeps the closed field looking like the rest of
 * the form and draws its own rounded panel for the options.
 *
 * The panel is rendered into a portal on `document.body` with `position:
 * fixed`, rather than absolutely positioned inside the trigger's own
 * stacking context — a plain absolute panel gets clipped by any ancestor
 * with `overflow` set (e.g. `.modal`'s `overflow-y: auto`), which cut the
 * bottom options off whenever this was used inside a dialog.
 *
 * With `searchable`, the trigger becomes a combobox: the customer types
 * directly in the same field instead of a separate search box inside the panel.
 */
export function SelectMenu<T extends string>({
  id,
  value,
  options,
  onChange,
  disabled,
  className,
  searchable,
  searchHint,
  noResultsLabel,
  clearLabel,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
}: {
  id?: string;
  value: T;
  options: SelectMenuOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  className?: string;
  /** When true, the trigger is a combobox — type in the field to filter options. */
  searchable?: boolean;
  /** Shown as the input placeholder while the combobox panel is open (before the customer types). */
  searchHint?: string;
  noResultsLabel?: string;
  /** aria-label for the × button that appears once there's text/a value to clear (searchable only). */
  clearLabel?: string;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [placement, setPlacement] = useState<PanelPlacement | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonTriggerRef = useRef<HTMLButtonElement>(null);
  const inputTriggerRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const openRef = useRef(false);
  /** True once the customer has typed in the combobox since it opened — used
   * so an open-with-empty-query browse does not clear the selection on blur. */
  const queryDirtyRef = useRef(false);
  /** Blocks one refocus-driven reopen after the field label is clicked to dismiss. */
  const suppressOpenRef = useRef(false);
  const listId = useId();
  const selected = options.find((o) => o.value === value) ?? options[0];
  const emptyOption = options.find((o) => o.value === '');
  const inputPlaceholder = open
    ? query.trim() === '' && searchHint
      ? searchHint
      : undefined
    : emptyOption?.label;

  const filteredOptions = useMemo(() => {
    if (!searchable) return options;
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (opt) =>
        opt.value !== '' &&
        (opt.label.toLowerCase().includes(q) || (opt.sublabel?.toLowerCase().includes(q) ?? false)),
    );
  }, [options, query, searchable]);

  function closeMenu() {
    openRef.current = false;
    setOpen(false);
    setPlacement(null);
    setQuery('');
  }

  /** Backspacing the combobox text out to nothing is never wired through
   * onChange (only picking an option or the clear button commits a value) —
   * so leaving the field empty and clicking/tabbing away used to silently
   * restore whatever was selected before, since `value` itself never
   * changed. Treat "closed after the customer cleared the typed query" as
   * an explicit clear — but not a plain open-then-blur that never typed,
   * which starts with an empty query on purpose so the full list shows. */
  function closeMenuCommittingEmpty() {
    if (searchable && queryDirtyRef.current && query.trim() === '' && value !== '') onChange('' as T);
    closeMenu();
  }

  function openMenu() {
    if (disabled) return;
    // Idempotent: onFocus calls this unconditionally (unlike the pointerdown/
    // toggle/arrow-down paths, which all already guard on openRef). Without
    // this guard, a focus event arriving while already open (e.g. right
    // after clearValue's own setQuery('')) would re-prime the query from the
    // stale `selected` closure and stomp whatever was just typed/cleared.
    if (openRef.current) return;
    openRef.current = true;
    setOpen(true);
    if (searchable) {
      // Start with an empty filter so the full catalogue (e.g. free models
      // sorted to the top) stays visible. Priming with the selected label
      // used to hide siblings whose id does not contain that substring —
      // e.g. picking fw-deepseek-v4-flash hid kimi-k2.5 while still showing
      // fw-deepseek-v4-flash-vision-exp.
      queryDirtyRef.current = false;
      setQuery('');
    }
  }

  function willRefocusInput(target: Node) {
    if (!(target instanceof HTMLLabelElement) || !id) return false;
    return target.htmlFor === id;
  }

  function handleComboboxPointerDown() {
    suppressOpenRef.current = false;
    if (!openRef.current) openMenu();
  }

  function getTriggerEl() {
    return searchable ? inputTriggerRef.current : buttonTriggerRef.current;
  }

  useLayoutEffect(() => {
    if (!open) return;
    const labels = filteredOptions.map((opt) => opt.label);
    const reposition = () => setPlacement(placePanel(getTriggerEl(), labels, panelRef.current));
    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, filteredOptions]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      if (openRef.current && willRefocusInput(target)) suppressOpenRef.current = true;
      closeMenu();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function toggle() {
    if (open) {
      closeMenu();
      return;
    }
    openMenu();
  }

  function selectOption(opt: SelectMenuOption<T>) {
    onChange(opt.value);
    closeMenu();
    if (searchable) inputTriggerRef.current?.blur();
  }

  /** One click instead of backspacing the whole label/query out by hand.
   * Opens (or keeps open) with an empty query so the full option list shows
   * right away. openRef/setOpen are set directly, ahead of focus(), so the
   * onFocus→openMenu() the focus() call triggers is a no-op (see the guard
   * there) instead of re-priming the query from the now-stale selection. */
  function clearValue(e: MouseEvent) {
    e.stopPropagation();
    onChange('' as T);
    openRef.current = true;
    setOpen(true);
    queryDirtyRef.current = false;
    setQuery('');
    inputTriggerRef.current?.focus();
  }

  function selectFirstMatch() {
    const first = filteredOptions.find((opt) => opt.value !== '');
    if (first) selectOption(first);
  }

  const rootClass = ['select-menu', open ? 'is-open' : '', searchable ? 'is-searchable' : '', className]
    .filter(Boolean)
    .join(' ');

  const closedLabel =
    value && selected?.value
      ? selected.badge
        ? `${selected.label} · ${selected.badge}`
        : selected.label
      : '';
  const comboboxValue = open ? query : closedLabel;
  const showClear = searchable && !disabled && comboboxValue !== '';

  return (
    <div className={rootClass} ref={rootRef}>
      {searchable ? (
        <div className="select-menu-combobox">
          <input
            id={id}
            ref={inputTriggerRef}
            type="text"
            className="select-menu-trigger select-menu-combobox-input"
            role="combobox"
            aria-autocomplete="list"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listId}
            aria-label={ariaLabel}
            aria-invalid={ariaInvalid || undefined}
            disabled={disabled}
            value={comboboxValue}
            placeholder={inputPlaceholder}
            autoComplete="off"
            onPointerDown={handleComboboxPointerDown}
            onFocus={() => {
              if (suppressOpenRef.current) {
                suppressOpenRef.current = false;
                inputTriggerRef.current?.blur();
                return;
              }
              openMenu();
            }}
            onChange={(e) => {
              queryDirtyRef.current = true;
              setQuery(e.target.value);
              if (!openRef.current) openMenu();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                selectFirstMatch();
              } else if (e.key === 'Escape') {
                closeMenuCommittingEmpty();
              } else if (e.key === 'ArrowDown' && !open) {
                openMenu();
              }
            }}
            onBlur={() => {
              // Defer so a list-item mousedown/click can run before we close.
              window.setTimeout(() => {
                const active = document.activeElement;
                if (panelRef.current?.contains(active) || rootRef.current?.contains(active)) return;
                closeMenuCommittingEmpty();
              }, 0);
            }}
          />
          <div className="select-menu-combobox-actions">
            {showClear && (
              <button
                type="button"
                className="select-menu-combobox-clear"
                aria-label={clearLabel}
                tabIndex={-1}
                onMouseDown={(e) => e.preventDefault()}
                onClick={clearValue}
              >
                <span className="select-menu-clear-icon" aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              className="select-menu-combobox-toggle"
              aria-label={ariaLabel}
              disabled={disabled}
              tabIndex={-1}
              onMouseDown={(e) => e.preventDefault()}
              onClick={toggle}
            >
              <span className="select-menu-chevron" aria-hidden="true" />
            </button>
          </div>
        </div>
      ) : (
        <button
          id={id}
          ref={buttonTriggerRef}
          type="button"
          className="select-menu-trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listId}
          aria-label={ariaLabel}
          aria-invalid={ariaInvalid || undefined}
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={toggle}
        >
          <span className="select-menu-value">{closedLabel || selected?.label}</span>
          <span className="select-menu-chevron" aria-hidden="true" />
        </button>
      )}
      {open &&
        createPortal(
          <div
            ref={panelRef}
            className={`select-menu-panel${className ? ` ${className}` : ''}`}
            style={{
              position: 'fixed',
              left: placement?.left ?? -9999,
              width: placement?.width,
              top: placement?.top,
              bottom: placement?.bottom,
              maxHeight: placement?.maxHeight,
              visibility: placement ? 'visible' : 'hidden',
            }}
          >
            <div className="select-menu-list" role="listbox" id={listId} aria-label={ariaLabel}>
              {filteredOptions.length === 0 ? (
                <p className="select-menu-empty">{noResultsLabel}</p>
              ) : (
                filteredOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={opt.value === value}
                    className={`select-menu-item${opt.value === value ? ' is-active' : ''}${opt.avatarText || opt.sublabel || opt.badge ? ' select-menu-item--rich' : ''}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectOption(opt)}
                  >
                    {opt.avatarText && (
                      <span className="select-menu-item-avatar" aria-hidden="true">
                        {opt.avatarText}
                      </span>
                    )}
                    {opt.sublabel ? (
                      <span className="select-menu-item-text">
                        <span className="select-menu-item-label">{opt.label}</span>
                        <span className="select-menu-item-sublabel">{opt.sublabel}</span>
                      </span>
                    ) : (
                      <span className="select-menu-item-label">{opt.label}</span>
                    )}
                    {opt.badge && <span className="select-menu-item-badge">{opt.badge}</span>}
                  </button>
                ))
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
